import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModuleJsonModel } from '../module.json.model';
import { Store } from '@ngxs/store';
import { LearningUnitState } from '../../learning-unit/ngxs/learning-unit.state';
import { LearningUnitActions } from '../../learning-unit/ngxs/learning-unit.actions';
import { MatIcon } from '@angular/material/icon';
import { MediaType } from '../../core/types';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetJsonModel } from '../../asset/assets.json.model';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { LearningUnitJsonModel } from '../../learning-unit/learning-unit.json.model';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { AuthState } from '../../core/authentication/auth.state';
import { SchoolClassState } from '../../core/global-enums/school-class/ngxs/school-class.state';
import { SchoolClassActions } from '../../core/global-enums/school-class/ngxs/school-class.actions';
import { EditorRole } from '../../editor/editor.role.enum';
import { MatDividerModule } from '@angular/material/divider';
import { ConsumerJsonModel } from '../../consumer/consumer.json.model';
import { AppSelectors } from '../../app.state';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { MultiComboSearchComponent } from '../../utils/component/multi-combo-search/multi-combo-search.component';
import { CollectionFilter } from '../../core/jsonApi/BaseCollectionService';
import { FetchOptionsActions } from '../../core/state/fetchOptions.actions';

@Component({
  selector: 'pixl-edit-module-management',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDividerModule,
    CommonModule,
    MatIcon,
    CdkDropList,
    DragDropModule,
    StateFilterCBComponent,
    MultiComboSearchComponent,
  ],
  templateUrl: './edit-module.component.html',
  styleUrl: './edit-module.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditModuleComponent implements OnInit {
  @ViewChild(StateFilterCBComponent) stateFilter!: StateFilterCBComponent;

  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private appState$ = this.store.select(AppSelectors.appState);

  public editor$ = this.store.select(AuthState.getEditor);
  public isZavAdmin = false;

  public imageUrl = signal('');
  preview = '';

  public consumers: ConsumerJsonModel[] = [];
  public subjects: SubjectJsonModel[] = [];

  learningUnits$ = this.store.select(LearningUnitState.getLearningUnits);
  schoolClasses$ = this.store.select(SchoolClassState.getSchoolClasses);

  status = '';

  public data = inject<ModuleJsonModel>(MAT_DIALOG_DATA);

  public searchTerm = '';
  public selectedContent: LearningUnitJsonModel | null = null;
  public currentPage = 1;
  learningUnits = this.data.learningUnits;

  public formGroup: FormGroup = this.fb.group({
    preTitle: this.data.preTitle || null,
    title: [this.data.title, Validators.required],
    description: this.data.description || null,
    preview: [this.data.preview, []],
    content: this.fb.array((this.data.content || []).map(c => this.fb.control(c))),
    status: [this.status, []],
    consumer: this.data.consumer || null,
    subjects: [this.data.subjects || []],
    schoolClasses: [this.data.schoolClasses || []],
    learningUnits: [this.data.learningUnits || []],
  });

  constructor() {
    this.buildURLs();
    this.status = this.data.status || 'Edit';
    this.destroyRef.onDestroy(() => this.resetFetch());
  }

  ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.subjects = subjects;
      }
    });
    this.store.dispatch([
      new LearningUnitActions.Fetch('', {
        pageNumber: 1,
        pageSize: -1,
        skipStoreDispatch: true
      }),
      new SchoolClassActions.Fetch({
        pageNumber: 1,
        pageSize: -1,
        skipStoreDispatch: true
      })
    ]);
    this.editor$.subscribe(editor => {
      if (editor && editor.role === EditorRole.ZAVAdmin) {
        this.isZavAdmin = true;
      }
    });

    // reset any filtered learningUnits
    this.resetFetch();
  }

  reorderLearningUnits(event: CdkDragDrop<LearningUnitJsonModel[]>) {
    const control = this.formGroup.get('learningUnits');
    if (control) {
      const current = [...(control.value || [])];
      moveItemInArray(current, event.previousIndex, event.currentIndex);
      control.setValue(current);
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.data.learningUnits, event.previousIndex, event.currentIndex);
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: ''
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetJsonModel) => {
        if (answer) {
          if (!this.data.preview) {
            this.data.preview = {
              id: '',
              name: '',
              mediaType: '',
              media_url: ''
            };
          }

          this.data.preview.id = answer.id;
          this.data.preview.mediaType = 'Image';
          this.data.preview.media_url = answer.media_url;
          this.data.preview.name = answer.name;

          this.cdr.detectChanges();
          this.buildURLs();
        }
      });
  }

  receiveData(event: LearningUnitJsonModel[] | null) {
    let count = 0;
    event?.forEach(value => {
      if (!this.formGroup.value.learningUnits?.includes(value)) {
        this.formGroup.value.learningUnits?.push(value);
      }
    });
    this.formGroup.value.learningUnits?.forEach((value: LearningUnitJsonModel) => {
      if (!event?.includes(value)) {
        this.formGroup.value.learningUnits?.splice(count, 1);
      }
      count++;
    });
  }

  receiveSearchQuery(event: string) {
    this.searchTerm = event;
    this.onSearchChange();
  }

  onSearchChange() {
    if (!this.searchTerm) {
      this.resetFetch();
      return;
    }

    const filters: CollectionFilter[] = [];

    filters.push({ fieldName: 'titleLike', value: this.searchTerm });

    this.store.dispatch([
      new FetchOptionsActions.UpdateFilters(filters),
      new FetchOptionsActions.ToDefault(),
    ]);

    this.store.dispatch(new LearningUnitActions.Fetch(undefined, {
      pageNumber: 1,
      pageSize: -1,
      skipStoreDispatch: true
    }));
  }

  resetFetch() {
    this.store.dispatch([
      new FetchOptionsActions.UpdateSortFieldName(''),
      new FetchOptionsActions.UpdateFilters([]),
    ]);

    this.store.dispatch(new LearningUnitActions.Fetch(undefined, {
      pageNumber: 1,
      pageSize: -1,
      skipStoreDispatch: true
    }));

    this.searchTerm = '';
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';
    if (this.data.preview && this.data.preview.media_url) {
      this.imageUrl.set(baseUrl + this.data.preview.media_url);
    }
  }

  public deleteMediaEntry(): void {
    const id = this.data.preview?.id;
    const name = this.data.preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: id,
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            this.data.preview = null;
            this.cdr.detectChanges();
            dialogRef.close();
          }
        },
      });
  }

  public getImageName(): string {
    if (this.data.preview && this.data.preview.name) {
      return this.data.preview.name;
    }
    return '';
  }

  get contentControls(): FormArray {
    return this.formGroup.get('content') as FormArray;
  }

  addContent() {
    this.contentControls.push(this.fb.control(''));
    this.cdr.detectChanges();
  }

  removeContent(index: number) {
    this.contentControls.removeAt(index);
  }

  statusChanged(newStatus: string) {
    this.status = newStatus;
  }

  public checkForErrors(formControl: AbstractControl): string {
    if (formControl.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      const formData = this.formGroup.value;
      formData.content = formData.content.filter((c: unknown): c is string =>
        typeof c === 'string' && c.trim().length > 0
      );
      formData.preview = this.data.preview;
      formData.status = this.status;
      if (this.data.hasId()) {
        this.data.update(formData);
        this.dialogRef.close(this.data);
      }
      else {
        const module = {
          ...this.data,
          ...formData,
          schoolClasses: [...this.data.schoolClasses, ...(formData.schoolClasses ? formData.schoolClasses : [])],
          subjects: [...this.data.subjects, ...(formData.subjects ? formData.subjects : [])],
          learningUnits: [...this.data.learningUnits, ...(formData.learningUnits ? formData.learningUnits : [])]
        };
        this.dialogRef.close(module);
      }
    }
  }

  onCancel(): void {
    this.resetFetch(); // reset any search querys for learning units
    this.dialogRef.close();
  }
}
